/**
 * The offer a FREE account sees when it reaches a paid feature.
 *
 * `SubscriptionGate` hides a paid feature the frontend knows about. This is the
 * other half: the backend has refused a request with 402, which is the only
 * answer that counts - the gate can be wrong about the tier, out of date after
 * a cancellation, or simply absent on a page nobody gated. Mounted once in App,
 * it listens for the event the HTTP clients raise.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useI18nStore } from "@/stores/i18nStore";
import {
  PAYMENT_REQUIRED_EVENT,
  PaymentRequired,
} from "@/utils/paymentRequired";
import { Lock } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const UpgradeRequiredDialog: React.FC = () => {
  const [required, setRequired] = useState<PaymentRequired | null>(null);
  const { t } = useI18nStore();
  const navigate = useNavigate();

  useEffect(() => {
    const onRequired = (event: Event) => {
      setRequired((event as CustomEvent<PaymentRequired>).detail);
    };
    window.addEventListener(PAYMENT_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(PAYMENT_REQUIRED_EVENT, onRequired);
  }, []);

  if (!required) return null;

  const label =
    required.requiredTier === "PREMIUM"
      ? t.featureRequiresPREMIUM
      : t.featureRequiresPLUS;

  return (
    <Dialog open onOpenChange={() => setRequired(null)}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Lock className="h-6 w-6 text-indigo-600" />
            <DialogTitle>{label}</DialogTitle>
          </div>
          <DialogDescription>{t.upgradeToUnlock}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setRequired(null)}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            {t.close}
          </button>
          <button
            type="button"
            onClick={() => {
              setRequired(null);
              navigate("/traveler/subscription");
            }}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
          >
            {t.upgradeToUnlock}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeRequiredDialog;
